import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUnsavedGuard } from "@/hooks/useUnsavedGuard";
import { toast } from "sonner";

const STATUS = [
  { value: "ativa", label: "Ativa" },
  { value: "planejada", label: "Planejada" },
  { value: "encerrada", label: "Encerrada" },
];
const empty = (cursoId) => ({ nome: "", curso_id: cursoId || "", data_inicio: "", data_termino: "", status: "ativa", instituicao: "", unidade: "", local: "", responsavel: "", responsavel_id: "", instrutor_ids: [], monitores: [] });

// Exibição: "{grau} {nome de guerra}"; sem nome de guerra usa o nome; grau "Não se aplica" é omitido.
const displayInstrutor = (u) => {
  const grau = (u.grau_nome || "").trim();
  const nome = (u.nome_guerra || "").trim() || (u.name || "").trim();
  return grau && grau.toLowerCase() !== "não se aplica" ? `${grau} ${nome}`.trim() : nome;
};

export function TurmaFormDialog({ open, onOpenChange, turma, onSaved, lockCursoId }) {
  const { isAdmin, user } = useAuth();
  const [cursos, setCursos] = useState([]);
  const [instrutores, setInstrutores] = useState([]);
  const [respOpen, setRespOpen] = useState(false);
  const [addInstOpen, setAddInstOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const { setBaseline, guard } = useUnsavedGuard();

  useEffect(() => {
    if (!open) return;
    api.get("/cursos").then((r) => setCursos(r.data));
    api.get("/auth/instrutores/ativos").then((r) => setInstrutores(r.data)).catch(() => setInstrutores([]));
    let f;
    if (turma) {
      f = { nome: turma.nome || "", curso_id: turma.curso_id || "", data_inicio: turma.data_inicio || "", data_termino: turma.data_termino || "", status: turma.status || "ativa", instituicao: turma.instituicao || "", unidade: turma.unidade || "", local: turma.local || "", responsavel: turma.responsavel || "", responsavel_id: turma.responsavel_id || "", instrutor_ids: turma.instrutor_ids || [], monitores: turma.monitores || [] };
    } else {
      f = empty(lockCursoId);
      // Instrutor criando: já vem selecionado como Responsável
      if (user?.role === "instrutor" && user?.id) {
        f.responsavel_id = user.id;
        f.responsavel = displayInstrutor(user);
      }
    }
    setForm(f); setBaseline(f);
  }, [open, turma, lockCursoId, user]); // eslint-disable-line

  const save = async () => {
    try {
      const payload = {
        ...form,
        instrutor_ids: Array.from(new Set(form.instrutor_ids || [])),
        monitores: Array.from(new Set((form.monitores || []).map((m) => m.trim()).filter(Boolean))),
      };
      if (turma) { await api.put(`/turmas/${turma.id}`, payload); toast.success("Turma atualizada"); }
      else { await api.post("/turmas", payload); toast.success("Turma criada"); }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const instrutorNome = (id) => instrutores.find((i) => i.id === id)?.display || (turma?.instrutores || []).find((i) => i.id === id)?.nome || id;
  const adicionarInstrutor = (id) => setForm((f) => f.instrutor_ids.includes(id) ? f : { ...f, instrutor_ids: [...f.instrutor_ids, id] });
  const removerInstrutor = (id) => setForm((f) => ({ ...f, instrutor_ids: f.instrutor_ids.filter((x) => x !== id) }));
  const adicionarMonitor = () => setForm((f) => ({ ...f, monitores: [...(f.monitores || []), ""] }));
  const setMonitor = (idx, v) => setForm((f) => ({ ...f, monitores: f.monitores.map((m, i) => (i === idx ? v : m)) }));
  const removerMonitor = (idx) => setForm((f) => ({ ...f, monitores: f.monitores.filter((_, i) => i !== idx) }));

  const cursosDisponiveis = (() => {
    const cursosBase = isAdmin ? cursos : cursos.filter((c) => c.permite_aplicacoes_instrutor);
    // Dedupe defensivo por nome (evita curso repetido no seletor caso o banco tenha duplicatas)
    return cursosBase.filter((c, i, arr) =>
      arr.findIndex((x) => (x.nome || "").trim().toLowerCase() === (c.nome || "").trim().toLowerCase()) === i);
  })();
  const cursoLocked = !!turma || !!lockCursoId;

  return (
    <Dialog open={open} onOpenChange={guard(onOpenChange, form)}>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>{turma ? "Editar turma" : "Nova turma"}</DialogTitle>
          <DialogDescription>Preencha os dados da turma/aplicação. O responsável é um instrutor ativo do sistema.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input data-testid="turma-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          <div>
            <Label>Curso</Label>
            <Select value={form.curso_id} onValueChange={(v) => setForm({ ...form, curso_id: v })} disabled={cursoLocked}>
              <SelectTrigger data-testid="turma-curso"><SelectValue placeholder="Selecione o curso" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {cursosDisponiveis.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isAdmin && (
            <p className="text-xs text-muted-foreground">Você só pode criar aplicações da Instrução de Primeira Intervenção. Informe a instituição/unidade abaixo.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Instituição</Label><Input data-testid="turma-instituicao" value={form.instituicao || ""} onChange={(e) => setForm({ ...form, instituicao: e.target.value })} placeholder="Ex.: PMERJ, Marinha, Exército, MP" /></div>
            <div><Label>Unidade / Organização</Label><Input data-testid="turma-unidade" value={form.unidade || ""} onChange={(e) => setForm({ ...form, unidade: e.target.value })} placeholder="Ex.: 27º BPM, BOPE, CPRV" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Local</Label><Input data-testid="turma-local" value={form.local || ""} onChange={(e) => setForm({ ...form, local: e.target.value })} /></div>
            <div>
              <Label>Responsável</Label>
              <Popover open={respOpen} onOpenChange={setRespOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" data-testid="turma-responsavel" className="w-full justify-between font-normal">
                    <span className="truncate">{form.responsavel || "Selecione o instrutor"}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-popover border-border w-[var(--radix-popover-trigger-width)] max-w-[90vw]" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar instrutor..." data-testid="responsavel-search" />
                    <CommandList>
                      <CommandEmpty>Nenhum instrutor ativo encontrado.</CommandEmpty>
                      <CommandGroup>
                        {instrutores.map((i) => (
                          <CommandItem key={i.id} value={i.display} data-testid={`responsavel-opt-${i.id}`}
                            onSelect={() => { setForm({ ...form, responsavel_id: i.id, responsavel: i.display }); setRespOpen(false); }}>
                            <Check className={cn("mr-2 h-4 w-4", form.responsavel_id === i.id ? "opacity-100" : "opacity-0")} />
                            {i.display}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Início</Label><Input type="date" data-testid="turma-inicio" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} /></div>
            <div><Label>Término</Label><Input type="date" data-testid="turma-termino" value={form.data_termino} onChange={(e) => setForm({ ...form, data_termino: e.target.value })} /></div>
          </div>
          <div>
            <Label>Situação</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="turma-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Instrutores (múltiplos, do banco de instrutores ativos) */}
          <div>
            <Label>Instrutores</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="turma-instrutores-list">
              {!(form.instrutor_ids || []).length && <span className="text-xs text-muted-foreground">Nenhum instrutor adicionado.</span>}
              {(form.instrutor_ids || []).map((iid) => (
                <Badge key={iid} data-testid={`instrutor-chip-${iid}`} className="bg-primary/15 text-primary border-primary/30 flex items-center gap-1">
                  {instrutorNome(iid)}
                  <button type="button" data-testid={`remover-instrutor-${iid}`} onClick={() => removerInstrutor(iid)} className="ml-0.5 hover:text-red-400"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
            <Popover open={addInstOpen} onOpenChange={setAddInstOpen}>
              <PopoverTrigger asChild>
                <Button type="button" size="sm" variant="secondary" data-testid="add-instrutor-btn" className="mt-2"><Plus className="h-4 w-4 mr-1.5" />Adicionar instrutor</Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 bg-popover border-border w-[280px] max-w-[90vw]" align="start">
                <Command>
                  <CommandInput placeholder="Buscar instrutor..." data-testid="add-instrutor-search" />
                  <CommandList>
                    <CommandEmpty>Nenhum instrutor disponível.</CommandEmpty>
                    <CommandGroup>
                      {instrutores.filter((i) => !(form.instrutor_ids || []).includes(i.id)).map((i) => (
                        <CommandItem key={i.id} value={i.display} data-testid={`add-instrutor-opt-${i.id}`}
                          onSelect={() => { adicionarInstrutor(i.id); setAddInstOpen(false); }}>
                          <Plus className="mr-2 h-4 w-4" />{i.display}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Monitores (digitação manual — não são usuários do sistema) */}
          <div>
            <Label>Monitores</Label>
            <p className="text-xs text-muted-foreground mb-1">Digitação manual — não são usuários do sistema.</p>
            <div className="space-y-1.5" data-testid="turma-monitores-list">
              {(form.monitores || []).map((m, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input data-testid={`monitor-input-${idx}`} value={m} onChange={(e) => setMonitor(idx, e.target.value)} placeholder="Ex.: 3º SGT Silva" />
                  <Button type="button" size="icon" variant="ghost" data-testid={`remover-monitor-${idx}`} onClick={() => removerMonitor(idx)} className="shrink-0"><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="secondary" data-testid="add-monitor-btn" className="mt-2" onClick={adicionarMonitor}><Plus className="h-4 w-4 mr-1.5" />Adicionar monitor</Button>
          </div>
        </div>
        <DialogFooter><Button data-testid="salvar-turma-btn" onClick={save} disabled={!form.nome || !form.curso_id}>{turma ? "Salvar alterações" : "Salvar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
